import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { popupState, authState } from "../../Recoil/RecoilState";
import VideosFilter from "../Shoplifting/VideosFilter";
import NoEntryFilter from "./NoEntryFilter";

const DEFAULT_STATUS_OPTIONS = [
  { value: "detected", label: "Detected" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "resolved", label: "Resolved" },
];

// statusOptions and FilterComponent let a feature with its own status vocabulary and
// filters (e.g. Lost & Found) reuse the tab; FilterComponent takes the same
// { filter, changeFilterHandle } props and ref.submit() as the built-in filters.
const IncidentVideosTab = ({
  featureName = "Incident Videos",
  featureKey = "no_entry",
  apiEndpoint = "",
  resolveEndpoint = "",
  statusOptions = DEFAULT_STATUS_OPTIONS,
  FilterComponent = null,
}) => {
  const [events, setEvents] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Evidence Gallery modal
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [activeEvidencePaths, setActiveEvidencePaths] = useState([]);
  const [activeEvidenceTitle, setActiveEvidenceTitle] = useState("");

  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);
  const childRef = useRef(null);

  const fetchEndpoint =
    apiEndpoint || `${baseURL}${featureKey.replace(/_/g, "-")}/events`;

  // Helper to build URL-safe video / snapshot link
  const resolveMediaUrl = (path) => {
    if (!path) return "";
    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:") ||
      path.startsWith("blob:")
    ) {
      return path;
    }
    const cleanBase = baseURL.replace(/\/+$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
  };

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build server-side query params according to API contract
      const params = {
        page,
        limit: itemsPerPage,
      };

      if (filter.status_filter) params.status_filter = filter.status_filter;
      if (filter.target_class) params.target_class = filter.target_class;
      if (filter.camera_id) params.camera_id = filter.camera_id;
      if (filter.zone_id) params.zone_id = filter.zone_id;
      if (filter.incident_id) params.incident_id = filter.incident_id;
      if (filter.start_date) params.start_date = filter.start_date;
      if (filter.end_date) params.end_date = filter.end_date;
      if (filter.start_time) params.start_time = filter.start_time;
      if (filter.end_time) params.end_time = filter.end_time;
      if (filter.location) params.location = filter.location;
      if (filter.building) params.building = filter.building;
      if (filter.floor_level) params.floor_level = filter.floor_level;
      if (filter.zone) params.zone = filter.zone;

      const { data } = await axios.get(fetchEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (data && Array.isArray(data.items)) {
        setEvents(data.items);
        setTotalItems(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setEvents(data);
        setTotalItems(data.length);
      } else if (data && Array.isArray(data.data)) {
        setEvents(data.data);
        setTotalItems(data.total ?? data.data.length);
      } else {
        setEvents([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.warn(`Error fetching ${featureName} events:`, err.message);
      setError(
        err.response?.data?.message ||
          `Unable to retrieve events from ${fetchEndpoint}. Check backend connection.`
      );
      setEvents([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [token, fetchEndpoint, featureName, page, filter]);

  useEffect(() => {
    if (token) {
      fetchIncidents();
    }
  }, [fetchIncidents, token]);

  const handleActionChange = async (id, newAction) => {
    const oldEvent = events.find((ev) => (ev.event_id || ev.incident_id || ev.id) === id);

    const updateFn = (list) =>
      list.map((ev) =>
        (ev.event_id || ev.incident_id || ev.id) === id
          ? { ...ev, status: newAction, action_taken: newAction }
          : ev
      );

    setEvents((prev) => updateFn(prev));

    try {
      let url = "";
      if (resolveEndpoint) {
        if (resolveEndpoint.includes("{event_id}")) {
          url = resolveEndpoint.replace("{event_id}", encodeURIComponent(id));
        } else if (resolveEndpoint.includes("{id}")) {
          url = resolveEndpoint.replace("{id}", encodeURIComponent(id));
        } else if (resolveEndpoint.endsWith("/resolve")) {
          url = resolveEndpoint.replace(/\/resolve$/, `/${encodeURIComponent(id)}/resolve`);
        } else {
          url = `${resolveEndpoint.replace(/\/+$/, "")}/${encodeURIComponent(id)}/resolve`;
        }
      } else {
        url = `${baseURL}${featureKey.replace(/_/g, "-")}/events/${encodeURIComponent(id)}/resolve`;
      }

      await axios.post(
        url,
        {
          status: newAction,
          action_taken: newAction,
          description: `Status updated to ${newAction}`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err) {
      console.warn("Error updating event action:", err.message);
      const rollbackFn = (list) =>
        list.map((ev) =>
          (ev.event_id || ev.incident_id || ev.id) === id
            ? {
                ...ev,
                status: oldEvent?.status || statusOptions[0]?.value,
                action_taken: oldEvent?.action_taken || "",
              }
            : ev
        );

      setEvents((prev) => rollbackFn(prev));
    }
  };

  const openFilter = () => {
    const isNoEntry = featureKey === "no_entry" || featureKey === "no-entry-zone";

    setPopup({
      isOpen: true,
      title: `Filter ${featureName} Events`,
      content: FilterComponent ? (
        <FilterComponent
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
            setPage(1);
          }}
        />
      ) : isNoEntry ? (
        <NoEntryFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
            setPage(1);
          }}
        />
      ) : (
        <VideosFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
            setPage(1);
          }}
        />
      ),
      sendReq: () => {
        childRef.current?.submit();
      },
    });
  };

  const openEvidenceGallery = (paths, title) => {
    setActiveEvidencePaths(paths || []);
    setActiveEvidenceTitle(title || "Recorded Evidence");
    setGalleryModalOpen(true);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const getStatusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "resolved") return "success";
    if (s === "acknowledged" || s === "intervened") return "info";
    if (s === "detected" || s === "open" || s === "high" || s === "critical") return "error";
    if (s === "false_positive") return "default";
    return "warning";
  };

  if (loading && events.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 4, alignItems: "center", justifyContent: "space-between" }}>
        <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
        </DesBtn>

        {totalItems > 0 && (
          <Typography variant="body2" color="text.secondary">
            Total Recorded Events: <strong>{totalItems}</strong>
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {events.map((ev, idx) => {
          const id = ev.event_id || ev.incident_id || ev.id || `ev-${idx}`;
          const videoSrc = resolveMediaUrl(
            ev.video_path);
          const snapshotSrc = resolveMediaUrl(ev.snapshot_path);
          const evidenceList = Array.isArray(ev.evidence_paths)
            ? ev.evidence_paths
            : [];

          return (
            <Grid item xs={12} md={6} lg={4} key={id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: "#000",
                    borderRadius: "8px 8px 0 0",
                    overflow: "hidden",
                  }}
                >
                  {videoSrc ? (
                    <video
                      controls
                      autoPlay={false}
                      poster={snapshotSrc || undefined}
                      width="100%"
                      style={{
                        display: "block",
                        aspectRatio: "16/9",
                        backgroundColor: "#000",
                      }}
                    >
                      <source src={videoSrc} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : snapshotSrc ? (
                    <img
                      src={snapshotSrc}
                      alt="Incident Snapshot"
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        objectFit: "contain",
                        display: "block",
                        backgroundColor: "#000",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "16/9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      <VideocamIcon sx={{ mr: 1 }} /> No Media Available
                    </Box>
                  )}

                  {/* Status / Severity Badge */}
                  <Chip
                    label={String(ev.status || ev.severity || "Detected").replace(/_/g, " ")}
                    color={getStatusColor(ev.status || ev.severity)}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  />

                  {/* Target Class Chip */}
                  {ev.target_class && (
                    <Chip
                      label={`Class: ${ev.target_class}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        fontWeight: 600,
                        bgcolor: "rgba(0,0,0,0.7)",
                        color: "#fff",
                      }}
                    />
                  )}
                </Box>

                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" noWrap>
                      {ev.camera_name || `Camera ${ev.stream_id || "1"}`}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {ev.event_timestamp || ev.entered_at || ev.timestamp
                        ? new Date(
                            ev.event_timestamp || ev.entered_at || ev.timestamp
                          ).toLocaleString()
                        : "—"}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Zone:</strong> {ev.zone_name || ev.camera_zone || ev.zone || "—"}
                    {ev.camera_location && ` | ${ev.camera_location}`}
                    {ev.building && ` (${ev.building}${ev.floor_level ? `, Floor ${ev.floor_level}` : ""})`}
                  </Typography>

                  {/* Incident Metrics */}
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    {ev.dwell_seconds !== undefined && ev.dwell_seconds !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Dwell: ${ev.dwell_seconds}s`}
                      />
                    )}
                    {ev.confidence !== undefined && ev.confidence !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={`Conf: ${(ev.confidence > 1 ? ev.confidence : ev.confidence * 100).toFixed(0)}%`}
                      />
                    )}
                    {evidenceList.length > 1 && (
                      <Chip
                        size="small"
                        icon={<PhotoLibraryIcon />}
                        label={`${evidenceList.length} Evidences`}
                        clickable
                        onClick={() =>
                          openEvidenceGallery(
                            evidenceList,
                            `${ev.camera_name || "Camera"} - Evidence Files`
                          )
                        }
                      />
                    )}
                  </Stack>

                  {ev.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      {ev.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 0.5 }} />

                  <FormControl size="small" fullWidth sx={{ mt: "auto" }}>
                    <InputLabel>Incident Status</InputLabel>
                    <Select
                      value={ev.status || ev.action_taken || statusOptions[0]?.value}
                      label="Incident Status"
                      onChange={(e) => handleActionChange(id, e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {events.length === 0 && !loading && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              color="text.secondary"
              align="center"
              sx={{ py: 6 }}
            >
              No {featureName} incidents found matching criteria.
            </Typography>
          </Grid>
        )}
      </Grid>

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            size="large"
            onChange={(_, value) => {
              setPage(value);
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
          />
        </Box>
      )}

      {/* Evidence Gallery Dialog */}
      <Dialog
        open={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{activeEvidenceTitle}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {activeEvidencePaths.map((itemPath, i) => {
              const itemUrl = resolveMediaUrl(itemPath);
              const isVideo =
                itemPath.endsWith(".mp4") ||
                itemPath.endsWith(".webm") ||
                itemPath.includes("clip");
              return (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper elevation={2} sx={{ p: 1, bgcolor: "#000", borderRadius: 1 }}>
                    {isVideo ? (
                      <video
                        controls
                        src={itemUrl}
                        width="100%"
                        style={{ display: "block", aspectRatio: "16/9" }}
                      />
                    ) : (
                      <img
                        src={itemUrl}
                        alt={`Evidence ${i + 1}`}
                        style={{
                          width: "100%",
                          aspectRatio: "16/9",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="rgba(255,255,255,0.8)"
                      sx={{ p: 0.5, display: "block", textAlign: "center" }}
                    >
                      Evidence Item #{i + 1}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGalleryModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentVideosTab;
